import { ChangeDetectorRef, Component, OnInit, ViewChild, effect, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { RequisitionStatusProxyService } from '../../../proxies/order/requisition-status.proxy.service';
import { RequisitionProxyService } from '../../../proxies/order/requisition.proxy.service';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ExternalRequisition } from '../../../models/requisitions/interfaces/external-requisition';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { DepartmentProxyService } from '../../../proxies/user-master/department.proxy.service';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { ItemCategory, ItemMaster, ItemMasterFilter } from '../../../models/item/item-master/item-master';
import { Department } from '../../../models/department/department';
import { ConfirmationService } from 'primeng/api';
import { forkJoin } from 'rxjs';
import { UserProxyService } from '../../../proxies/user-master/user.proxy.service';
import { UserFull } from '../../../models/user-master/user-lite';
import { LocaleHelperService } from '../../../services/locale-helper.service';
import { BreadcrumbService, EnumValues, ModalService, Note, NotesSidebarComponent } from '@card-software/library';
import { EnumService } from '../../../services/enum.service';
import { setOptionsForPrimeNg } from '../../../PrimeNG/utils';
import { ExternalRequisitionType } from '../../../models/requisitions/enums/external-requisition-type';
import { ExternalRequisitionPriority } from '../../../models/requisitions/enums/external-requisition-priority';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { ExternalRequestEntry } from '../../../models/requisitions/interfaces/external-request-entry';
import { ExternalRequisitionStatus } from '../../../models/requisitions/interfaces/external-requisition-status';
import { TableQuotationsComparativeService } from '../../../services/table-quotations-comparative.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { QuotationProxyService } from '../../../proxies/order/quotation.proxy.service';
import _ from 'lodash';
import { LocationProxyService } from '../../../proxies/inventory/location.proxy.service';
import { QuotationSearchCommand } from '../../../models/order/quotation';
import { Table } from 'primeng/table';
import { ExternalRequisitionAdminGroup, ExternalRequisitionViewerGroup } from '../../../models/roles/roles-group';
import { Translation } from '../../../models/Translations';
import { AuthService, GlobalEntityDefinition } from '@card-software/nexus-utilities';

export interface RelatedPurchaseOrderItemRow {
  purchaseOrderReference: string;
  purchaseOrderNumber: string;
  itemCode: string;
  itemDisplayName?: Translation;
  quantity: number;
  status: number;
}

export interface ExternalRequestEntryForm {
  lineId: FormControl<string | null>;
  itemCode: FormControl<string>;
  comment: FormControl<string | null>;
  uom: FormControl<number>;
  quantity: FormControl<number>;
  approved: FormControl<boolean>;
}

@Component({
  selector: 'app-requisition-details',
  templateUrl: './requisition-details.component.html',
  styleUrl: './requisition-details.component.scss',
})
export class RequisitionDetailsComponent implements OnInit {
  @ViewChild('itemsToRequisitionTable') itemsToRequisitionTable!: Table;

  showQuotationStuff = false;
  newQuote = false;
  notes: Note[] = [];

  isLoading = 0;
  checked = false;
  isNew = false;
  uoms: EnumValues;
  requisition: ExternalRequisition | null = null;
  requisitionTypes: EnumValues = {};
  requisitionPriority: EnumValues = {};
  purchaseOrderStatuses: EnumValues = {};
  filteredItems: ItemMaster[] = [];
  allUsers: UserFull[] = [];
  usersToDisplay: UserFull[] = [];
  allDepartments: Department[] = [];
  departmentsToDisplay: Department[] = [];
  requisitionsToDisplay: ExternalRequisition[] = [];
  userLite: UserFull | null = null;
  allItems: ItemMaster[] = [];
  addedItemsCache: ItemMaster[] = []; // Cache to store items added to requisition
  allRequisitionStatus: ExternalRequisitionStatus[] = [];
  itemsToRequisitionFormArray = new FormArray<FormGroup<ExternalRequestEntryForm>>([]);
  itemsToRequisitionForTable: FormGroup<ExternalRequestEntryForm>[] = [];
  setOptionsForPrimeNg = setOptionsForPrimeNg;
  requisitionFormSubmitted = false;
  readonlyMode = false;
  allowApprove = false;
  hardApprove = false;
  visibleDeleteButton = true;
  approversCount = 0;
  // Form controls
  createdDateControl: FormControl<string | Date | null> = new FormControl({ value: new Date(), disabled: true });
  idControl = new FormControl('');
  trackingNumberControl = new FormControl('');
  statusReferenceControl: FormControl<Department | null | string> = new FormControl(null, [Validators.required]);
  requestingUsernameControl = new FormControl({ value: '', disabled: true });
  requestingAssignedUserControl = new FormControl<UserFull | null>(null);
  departmentReferenceControl: FormControl<Department | null> = new FormControl(null, [Validators.required]);
  priorityControl: FormControl<ExternalRequisitionPriority | null> = new FormControl(null, [Validators.required]);
  requisitionTypeControl = new FormControl<ExternalRequisitionType | null>(null, [Validators.required]);

  // Filter form
  filterForm = new FormGroup({
    name: new FormControl(''),
    itemCode: new FormControl(''),
    description: new FormControl(''),
    itemCategoryCode: new FormControl<ItemCategory | null>(null),
    itemLowestSubCategoryCode: new FormControl<ItemCategory | null>(null),
    inventoryTypeReference: new FormControl(null),
    itemCategoryReference: new FormControl(''),
  });

  // Main form
  destinationLocationCodeControl = new FormControl<string | null>(null, [Validators.required]);
  destinationWarehouseOptions: { code: string; label: string }[] = [];

  requisitionForm = new FormGroup({
    id: this.idControl,
    number: this.trackingNumberControl,
    statusReference: this.statusReferenceControl,
    departmentReference: this.departmentReferenceControl,
    createdDate: this.createdDateControl,
    requisitionType: this.requisitionTypeControl,
    priority: this.priorityControl,
    createdBy: this.requestingUsernameControl,
    assignedUser: this.requestingAssignedUserControl,
    destinationLocationCode: this.destinationLocationCodeControl,
  });

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private translateService = inject(TranslateService);
  private breadcrumbService = inject(BreadcrumbService);
  private statusRequisitionService = inject(RequisitionStatusProxyService);
  private requisitionService = inject(RequisitionProxyService);
  private departmentService = inject(DepartmentProxyService);
  private userService = inject(UserProxyService);
  private itemMasterService = inject(ItemMasterProxyService);
  private localeHelperService = inject(LocaleHelperService);
  private enumService = inject(EnumService);
  private userAuthenticationService = inject(UserAuthenticationService);
  private modalService = inject(ModalService);
  private tableQuotationsComparativeService = inject(TableQuotationsComparativeService);
  private quotationProxyService = inject(QuotationProxyService);
  private confirmationService = inject(ConfirmationService);
  private crt = inject(ChangeDetectorRef);
  private locationProxyService = inject(LocationProxyService);

  constructor() {
    effect(() => {
      this.requisitionTypes = this.enumService.getEnum('ExternalRequisitionType', 'orderEnums')
        ? this.enumService.getEnum('ExternalRequisitionType', 'orderEnums').values
        : {};
      this.requisitionPriority = this.enumService.getEnum('ExternalRequisitionPriority', 'orderEnums')
        ? this.enumService.getEnum('ExternalRequisitionPriority', 'orderEnums').values
        : {};
      this.purchaseOrderStatuses = this.enumService.getEnum('PurchaseOrderStatus', 'orderEnums')
        ? this.enumService.getEnum('PurchaseOrderStatus', 'orderEnums').values
        : {};
    });
    this.uoms = this.enumService.getEnum('UnitOfMeasure', 'itemEnums').values;
  }

  ngOnInit(): void {
    this.loadConsumableWarehouses();
    this.statusRequisitionService.getAllStatuses().subscribe({
      next: (response) => {
        if (response.data) {
          this.allRequisitionStatus = response.data;
          this.allRequisitionStatus.map((status) => {
            status.fieldToDisplay = this.localeHelperService.getLocalizedValue(status?.displayName);
            return status;
          });
        }
      },
      error: () => {
        /* intentionally empty */
      },
    });
    this.userLite = this.userAuthenticationService.userLite as unknown as UserFull | null;
    this.requestingUsernameControl.setValue(this.userLite?.username ?? '');
    this.loadInitialData();

    // Check if it's creation or edition based on route params
    this.route.queryParams.subscribe((params) => {
      const id = params['id'];
      if (!id) {
        this.router.navigate(['/requisitions']);
        return;
      }

      if (id === 'new') {
        // New requisition mode
        this.requisition = null;
        this.isNew = true;
        this.breadcrumbService.setCurrent('Common_New');
      } else {
        // Edit mode
        this.isLoading++;
        this.getRequisitionById(id);
      }
    });
  }

  private loadConsumableWarehouses(): void {
    this.locationProxyService.getConsumableWarehouses().subscribe({
      next: (response) => {
        if (response.data) {
          this.destinationWarehouseOptions = response.data.map((w) => ({
            code: w.locationCode,
            label: this.localeHelperService.getLocalizedValue(w.fullName ?? w.name) + ` (${w.locationCode})`,
          }));
        }
      },
    });
  }

  private loadInitialData(): void {
    this.isLoading++;
    forkJoin([
      this.userService.getAllUsers(),
      this.departmentService.getDepartmentsByMember(this.userLite?.username ?? ''),
      this.itemMasterService.postItemSearch({}, true, true),
    ]).subscribe({
      next: ([users, departments, itemsMaster]) => {
        this.allDepartments = departments.data || [];
        this.allDepartments.map((dept) => {
          dept.fieldToDisplay = this.localeHelperService.getLocalizedValue(dept?.description ?? '');
          return dept;
        });
        this.departmentsToDisplay = this.allDepartments;
        this.allUsers = users.data || [];
        this.allUsers.map((user) => {
          user.fieldToDisplay = `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim() || user.username;
          return user;
        });
        this.allItems = itemsMaster.data?.items || [];
        this.isLoading--;
      },
      error: () => {
        this.isLoading--;
      },
    });
  }

  private getRequisitionById(id: string): void {
    this.requisitionService.getRequisitionById(id).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.requisition = response.data;
          this.getQuotationForThisRequisition();
          this.populateRequisitionData(this.requisition);
          this.searchRequisition(this.requisition.number || '');
          this.breadcrumbService.setCurrent(this.requisition.number);
        }
        this.isLoading--;
      },
      error: (error) => {
        console.error('Error fetching requisition:', error);
        this.isLoading--;
      },
    });
  }

  private populateRequisitionData(requisition: ExternalRequisition): void {
    const assignedUserObj = requisition.assignUser ? this.allUsers.find((user) => user.username === requisition.assignUser) : null;

    this.requisitionForm.patchValue({
      id: requisition.id,
      number: requisition.number,
      statusReference: requisition.statusReference,
      departmentReference: this.allDepartments.find((dept) => dept.id === requisition.departmentReference) || null,
      createdDate: requisition.createdDate ? new Date(requisition.createdDate.toString()) : new Date(),
      requisitionType: requisition.requisitionType,
      priority: requisition.priority,
      createdBy: requisition.createdBy,
      assignedUser: assignedUserObj || null,
      destinationLocationCode: requisition.destinationLocationCode || null,
    });

    // Disable destination location if it was inherited from Supply Request
    if (requisition.destinationLocationCode) {
      this.destinationLocationCodeControl.disable();
    }
    this.departmentService.getDepartmentById(requisition.departmentReference || '').subscribe({
      next: (response) => {
        if (response.success && response.data) {
          response.data.fieldToDisplay = this.localeHelperService.getLocalizedValue(response.data?.description ?? '');
          this.departmentReferenceControl.setValue(response.data);
        }
      },
      error: (error) => {
        console.error('Error fetching department:', error);
      },
    });

    if (requisition.assignUser) {
      this.userService.getUserByUsername(requisition.assignUser).subscribe({
        next: (response) => {
          if (response.success && response.data) {
            response.data.fieldToDisplay =
              `${response.data.profile?.firstName || ''} ${response.data.profile?.lastName || ''}`.trim() || response.data.username;
            this.requestingAssignedUserControl.setValue(response.data);
          }
        },
        error: (error) => {
          console.error('Error fetching department:', error);
        },
      });
    }

    // Populate entries if they exist
    if (requisition.entries && requisition.entries.length > 0) {
      this.itemsToRequisitionFormArray.clear();
      requisition.entries.forEach((entry) => {
        const entryForm = new FormGroup<ExternalRequestEntryForm>({
          lineId: new FormControl(entry.lineId),
          itemCode: new FormControl(entry.itemCode, { validators: [Validators.required], nonNullable: true }),
          comment: new FormControl(entry.comment),
          uom: new FormControl(entry.uom, { validators: [Validators.required], nonNullable: true }),
          quantity: new FormControl(entry.quantity || 1, {
            validators: [Validators.required, Validators.min(1)],
            nonNullable: true,
          }),
          approved: new FormControl(entry.approved, { nonNullable: true }),
        });
        this.itemsToRequisitionFormArray.push(entryForm);
      });

      this.itemsToRequisitionForTable = [...this.itemsToRequisitionFormArray.controls];

      this.loadItemsForCache(requisition.entries.map((e) => e.itemCode));
    }

    // Lógica especial para requisitions con parentRequisitionReference
    // Solo el campo Assigned User debe estar habilitado
    if (this.hasParentRequisition()) {
      this.enableOnlyAssignedUserField();
    }
  }

  private loadItemsForCache(itemCodes: string[]): void {
    if (itemCodes.length === 0) return;

    this.itemMasterService.getItemCodesBatch({ itemCodes }).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          response.data.forEach((item) => {
            if (!this.addedItemsCache.find((i) => i.itemCode === item.itemCode)) {
              this.addedItemsCache.push(item);
            }
          });
        }
      },
      error: (error) => {
        console.error('Error loading items for cache:', error);
      },
    });
  }

  private saveManagerChanges(): void {
    console.log('Initiating saveManagerChanges with hardApprove:', this.hardApprove);

    // Check if any entries are approved
    const approvedEntries = this.itemsToRequisitionFormArray.controls.filter((control) => control.get('approved')?.value === true);
    const hasApprovedEntries = approvedEntries.length > 0;
    const totalEntries = this.itemsToRequisitionFormArray.length;

    // Build comprehensive warning message based on approval status
    let warningMessage = `<div class="text-left">`;
    warningMessage += `<p class="mb-3"><strong>📊 Status Summary:</strong></p>`;
    warningMessage += `<ul class="list-disc list-inside mb-4 space-y-1">`;
    warningMessage += `<li>Approved entries: <strong>${approvedEntries.length} / ${totalEntries}</strong></li>`;
    warningMessage += `<li>Total entries to process: <strong>${totalEntries}</strong></li>`;
    warningMessage += `</ul>`;

    if (!hasApprovedEntries) {
      warningMessage += `<div class="bg-yellow-50 border border-yellow-200 rounded p-3 mb-3">`;
      warningMessage += `<p class="text-yellow-800"><i class="pi pi-exclamation-triangle mr-2"></i><strong>Warning:</strong> No entries are currently approved</p>`;
      warningMessage += `</div>`;
    }

    warningMessage += `<p class="text-sm text-gray-600">You can enable "Hard Approve Mode" to automatically approve entries during save.</p>`;
    warningMessage += `</div>`;

    // Show the custom confirmation dialog
    this.confirmationService.confirm({
      message: warningMessage,
      header: 'Save Requisition Changes',
      key: 'save-dialog',
      icon: hasApprovedEntries ? 'pi pi-question-circle' : 'pi pi-exclamation-triangle',
      acceptLabel: 'Proceed with Save',
      rejectLabel: 'Cancel',
      accept: () => {
        console.log('User confirmed save with hardApprove:', this.hardApprove);

        // Show additional warning if hard approve is enabled with no approved entries
        if (this.hardApprove && !hasApprovedEntries) {
          this.confirmationService.confirm({
            message:
              `<div class="text-center">` +
              `<p class="text-red-600 font-semibold mb-3">⚠️ CRITICAL WARNING</p>` +
              `<p class="mb-3">You are about to <strong>Hard Approve</strong> a requisition with <strong>NO approved entries</strong>.</p>` +
              `<p class="text-sm text-gray-600">This may cause system errors or workflow issues.</p>` +
              `</div>`,
            header: 'Confirm Hard Approve',
            key: 'main',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Force Hard Approve',
            rejectLabel: 'Cancel',
            accept: () => {
              console.log('User confirmed hard approve with no approved entries - proceeding');
              this.saveManagerChange();
            },
          });
        } else {
          this.saveManagerChange();
        }
      },
      reject: () => {
        console.log('User cancelled save changes');
      },
    });
  }

  public saveManagerChange(): void {
    if (_.isNil(this.requisition)) {
      return;
    }
    const requisition: ExternalRequisition = {
      ...this.requisition,
      entries: this.itemsToRequisitionFormArray.value.map((entry) => ({
        ...entry,
        // Preserve existing lineId for existing entries, null for new entries
        lineId: entry.lineId || null,
      })) as ExternalRequestEntry[],
    };

    this.requisitionService.saveManagerChanges(requisition, this.hardApprove).subscribe({
      next: (response) => {
        if (response.success) {
          GlobalMessageUtil.success('Success', 'Manager changes saved successfully');
          this.router.navigate(['/requisitions']);
        } else {
          GlobalMessageUtil.error('Error', 'Error saving manager changes');
        }
      },
      error: (error) => {
        console.error('Error saving manager changes:', error);
      },
    });
  }

  private proceedWithSave(): void {
    const formData = this.requisitionForm.getRawValue() as unknown as Partial<ExternalRequisition>;
    const entriesData = this.itemsToRequisitionFormArray.value as unknown as ExternalRequestEntry[];

    if (this.requisition?.id) {
      // Edit mode
      this.updateRequisition(formData, entriesData);
    } else {
      // Create mode
      this.createRequisition(formData, entriesData);
    }
  }

  saveRequisition(): void {
    this.requisitionFormSubmitted = true;

    if (this.hasParentRequisition() && this.requisition?.id) {
      this.updateRequisitionAssignedUser();
      return;
    }

    if (this.itemsToRequisitionFormArray.length === 0) {
      GlobalMessageUtil.error('Error', 'At least one entry must be added to the requisition');
      return;
    }
    if (this.itemsToRequisitionFormArray.invalid) {
      GlobalMessageUtil.error('Error', 'Please ensure all entries have valid data');
      return;
    }

    //we disabled some fields when approving
    // so we need to handle before main form validation
    if (this.checked) {
      this.saveManagerChanges();
      return;
    }

    const assignedUserValue = this.requestingAssignedUserControl.value;
    if (assignedUserValue) {
      const assignedUsername = assignedUserValue?.username || assignedUserValue;
      if (assignedUsername === this.userLite?.username) {
        GlobalMessageUtil.error('Validation Error', 'The assigned user cannot be the same as the requesting user');
        return;
      }
    }

    if (!this.requisitionForm.valid) {
      this.showValidationErrors();
      return;
    }

    const formData = this.requisitionForm.getRawValue() as unknown as Partial<ExternalRequisition>;
    const entriesData = this.itemsToRequisitionFormArray.value as unknown as ExternalRequestEntry[];
    if (this.requisition?.id) {
      // Edit mode
      this.updateRequisition(formData, entriesData);
    } else {
      // Create mode
      this.createRequisition(formData, entriesData);
    }
  }

  private createRequisition(formData: Partial<ExternalRequisition>, entriesData: ExternalRequestEntry[]): void {
    this.isLoading++;

    const newRequisition = {
      ...formData,
      entries: entriesData.map((entry) => ({
        ...entry,
        lineId: null, // Let the API generate new ObjectIds for new entries
      })),
      notes: [],
      requiredApprovers: 0,
      statusReference: (this.statusReferenceControl.value as string) || '',
      assignUser: (this.requestingAssignedUserControl.value?.username || this.requestingAssignedUserControl.value || undefined) as
        string | undefined,
      destinationLocationCode: this.destinationLocationCodeControl.value || '',
    };
    newRequisition.departmentReference = this.departmentReferenceControl.value?.id || '';
    console.log('New Requisition to create:', newRequisition);
    this.requisitionService.upsertRequisition(newRequisition as unknown as ExternalRequisition).subscribe({
      next: (response) => {
        GlobalMessageUtil.success('Success', `Requisition created successfully` + response.data?.number);
        this.router.navigate(['/requisitions']);
        this.isLoading--;
      },
      error: (error) => {
        console.error('Error creating requisition:', error);
        this.isLoading--;
      },
    });
  }

  private updateRequisition(formData: Partial<ExternalRequisition>, entriesData: ExternalRequestEntry[]): void {
    this.isLoading++;

    // Check if current status is "Awaiting Creator Changes" and user is the creator
    const matchedStatus = this.allRequisitionStatus.find((status) => status.id === this.requisition?.statusReference);
    const isAwaitingCreatorChanges =
      matchedStatus && this.localeHelperService.getLocalizedValue(matchedStatus.displayName) === 'Awaiting Creator Changes';
    const isCreator = this.requisition?.createdBy === this.userLite?.username;

    // Find "Pending Approval" status
    let statusReference: string = (this.statusReferenceControl.value as string) || '';
    if (isAwaitingCreatorChanges && isCreator) {
      const pendingApprovalStatus = this.allRequisitionStatus.find(
        (status) => this.localeHelperService.getLocalizedValue(status.displayName) === 'Pending Approval',
      );
      if (pendingApprovalStatus) {
        statusReference = pendingApprovalStatus.id || '';
      }
    }

    const updatedRequisition: ExternalRequisition = {
      ...this.requisition!,
      ...formData,
      entries: entriesData.map((entry) => ({
        ...entry,
        // Preserve existing lineId for existing entries, null for new entries
        lineId: entry.lineId || null,
      })),
      departmentReference: this.requisition?.departmentReference || '',
      requiredApprovers: this.requisition?.requiredApprovers || 0,
      statusReference: statusReference,
      assignUser: (this.requestingAssignedUserControl.value?.username || this.requestingAssignedUserControl.value || undefined) as
        string | undefined,
      destinationLocationCode: this.requisition?.destinationLocationCode || this.destinationLocationCodeControl.value || '',
    };

    this.requisitionService.upsertRequisition(updatedRequisition).subscribe({
      next: () => {
        GlobalMessageUtil.success('Success', `Requisition updated successfully`);
        this.router.navigate(['/requisitions']);
        this.isLoading--;
      },
      error: (error) => {
        console.error('Error updating requisition:', error);
        this.isLoading--;
      },
    });
  }
  private updateRequisitionAssignedUser(): void {
    this.isLoading++;

    const updatedRequisition: ExternalRequisition = {
      ...this.requisition!,
      assignUser: this.requestingAssignedUserControl.value?.username,
    };

    console.log('Updating assigned user for parent requisition:', updatedRequisition.assignUser);

    this.requisitionService.upsertRequisition(updatedRequisition).subscribe({
      next: () => {
        GlobalMessageUtil.success('Success', `Assigned user updated successfully for ${this.requisition?.number}`);
        this.router.navigate(['/requisitions']);
        this.isLoading--;
      },
      error: (error) => {
        console.error('Error updating assigned user:', error);
        this.isLoading--;
      },
    });
  }

  onViewMore(): void {
    this.openNotesModal();
  }

  openNotesModal(): void {
    const modalData = {
      notes: this.notes,
      userLite: this.userLite,
      viewOnly: true,
    };

    this.modalService.open(NotesSidebarComponent, {}, modalData).subscribe({
      next: (result) => {
        if (result && result.action === 'noteAdded') {
          this.notes = result.data.notes;
        }
      },
      error: (error) => {
        console.error('Error handling notes modal result:', error);
      },
    });
    setTimeout(() => {
      const modalElement = document.querySelector('.panel.modal') as HTMLElement;
      if (modalElement) modalElement.style.maxWidth = '50%';
      modalElement.style.width = '50%';
    }, 100);
  }

  private showValidationErrors(): void {
    const invalidFields: string[] = [];

    if (this.requisitionForm.get('departmentReference')?.invalid) {
      invalidFields.push('Department');
    }
    if (this.requisitionForm.get('requisitionType')?.invalid) {
      invalidFields.push('Requisition Type');
    }
    if (this.requisitionForm.get('priority')?.invalid) {
      invalidFields.push('Priority');
    }
    if (this.requisitionForm.get('destinationLocationCode')?.invalid && !this.requisition?.destinationLocationCode) {
      invalidFields.push('Destination Warehouse');
    }

    if (invalidFields.length > 0) {
      GlobalMessageUtil.error('Required Fields Missing', `Please fill the following required fields: ${invalidFields.join(', ')}`);
    }
  }

  cancel(): void {
    this.router.navigate(['/requisitions']);
  }

  searchDepartment(event: AutoCompleteCompleteEvent): void {
    const query = event.query.toLowerCase();
    this.departmentsToDisplay = this.allDepartments.filter((dept) =>
      this.localeHelperService
        .getLocalizedValue(dept?.description ?? '')
        .toLowerCase()
        .includes(query),
    );
    if (this.departmentsToDisplay.length > 8) {
      this.departmentsToDisplay = this.departmentsToDisplay.slice(0, 8);
    }
  }

  applyFilters(): void {
    this.isLoading++;

    const filterData: ItemMasterFilter = {
      name: this.filterForm.get('description')?.value || undefined,
      itemCode: this.filterForm.get('description')?.value || undefined,
    };

    this.itemMasterService.postItemSearch(filterData, true, true).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.allItems = response.data?.items || [];
          this.allItems.map((item) => {
            item.fieldToDisplay = this.localeHelperService.getLocalizedValue(item.itemDescription ?? '');
            return item;
          });
        }
        this.isLoading--;
      },
      error: (error) => {
        console.log('Error applying filters:', error);
        this.isLoading--;
      },
    });
  }

  resetFilters(): void {
    this.filterForm.reset();
    this.getAllItems();
  }

  getAllItems(): void {
    this.isLoading++;
    this.itemMasterService.postItemSearch({}, true, true).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.allItems = response.data?.items || [];
        }
        this.isLoading--;
      },
      error: (error) => {
        console.error('Error getting all items:', error);
        this.isLoading--;
      },
    });
  }

  addItemToRequisition(item: ItemMaster): void {
    // Check if item is already added
    const existingEntry = this.itemsToRequisitionFormArray.controls.find((control) => control.get('itemCode')?.value === item.itemCode);

    if (existingEntry) {
      GlobalMessageUtil.warn('Item Already Added', 'This item is already in the requisition');
      return;
    }

    // Add item as entry to requisition
    const entryForm = new FormGroup<ExternalRequestEntryForm>({
      lineId: new FormControl(null),
      itemCode: new FormControl(item.itemCode || '', {
        validators: [Validators.required],
        nonNullable: true,
      }),
      comment: new FormControl(null),
      uom: new FormControl(item.unitOfMeasure, {
        // Default UOM, you might want to get this from item
        validators: [Validators.required],
        nonNullable: true,
      }),
      quantity: new FormControl(1, {
        validators: [Validators.required, Validators.min(1)],
        nonNullable: true,
      }),
      approved: new FormControl(false, { nonNullable: true }),
    });

    this.itemsToRequisitionFormArray.push(entryForm);
    this.itemsToRequisitionForTable = [...this.itemsToRequisitionFormArray.controls];

    if (!this.addedItemsCache.find((i) => i.itemCode === item.itemCode)) {
      this.addedItemsCache.push(item);
    }

    GlobalMessageUtil.success('Item Added', 'Item successfully added to requisition');
  }

  removeItemFromRequisition(index: number): void {
    if (index >= 0 && index < this.itemsToRequisitionFormArray.length) {
      this.itemsToRequisitionFormArray.removeAt(index);

      // Create a new array reference using spread operator so PrimeNG detects the change
      this.itemsToRequisitionForTable = [...this.itemsToRequisitionFormArray.controls];

      GlobalMessageUtil.info('Entry Removed', 'Entry removed from requisition');
    }
  }

  get itemsToRequisition(): FormGroup<ExternalRequestEntryForm>[] {
    return this.itemsToRequisitionFormArray.controls;
  }

  get isNewRequisition(): boolean {
    return !this.requisition?.id;
  }

  get relatedPurchaseOrderItems(): RelatedPurchaseOrderItemRow[] {
    if (!this.requisition?.relatedPurchaseOrders) {
      return [];
    }
    return this.requisition.relatedPurchaseOrders.flatMap((po) =>
      po.entries.map((entry) => ({
        purchaseOrderReference: po.purchaseOrderReference,
        purchaseOrderNumber: po.purchaseOrderNumber,
        itemCode: entry.itemCode,
        itemDisplayName: entry.itemDisplayName,
        quantity: entry.quantity,
        status: po.status,
      })),
    );
  }

  getPurchaseOrderStatusLabel(status: number): string {
    return this.purchaseOrderStatuses[status] || `Status ${status}`;
  }

  isDisabledAddButton(item: ItemMaster): boolean {
    return this.itemsToRequisitionFormArray.controls.some((control) => control.get('itemCode')?.value === item.itemCode);
  }
  // Helper method to check if field has validation errors
  hasFieldError(fieldName: string): boolean {
    const field = this.requisitionForm.get(fieldName);
    return !!(this.requisitionFormSubmitted && field && field.invalid);
  }

  getItemDescription(itemCode: string): string {
    let item = this.addedItemsCache.find((i) => i.itemCode === itemCode);

    if (!item) {
      item = this.allItems.find((i) => i.itemCode === itemCode);
    }

    return item ? this.localeHelperService.getLocalizedValue(item.itemDescription || '') : '';
  }

  _setOptionsForPrimeNg(value: EnumValues, showOnlyLbs = false): { label: string; value: number }[] {
    let options = Object.keys(value).map((key) => {
      const numKey = parseInt(key);
      return {
        label: value[numKey],
        value: numKey,
      };
    });
    if (showOnlyLbs) {
      options = options.filter((opt) => opt.value === 3); // Assuming 3 is the code for lbs
    }
    return options;
  }

  searchRequisition(requisitionNumber: string): void {
    if (requisitionNumber) {
      this.requisitionService.searchRequisition({ number: requisitionNumber }, true, false, 1, 9999).subscribe({
        //EXT-REQ-8 , EXT-REQ-81
        next: (response) => {
          if (response.success && response.data) {
            this.readonlyMode = true;
            const matchedStatus = this.allRequisitionStatus.find((status) => status.id === this.requisition?.statusReference);
            if (matchedStatus && this.localeHelperService.getLocalizedValue(matchedStatus.displayName) === 'Draft') {
              this.readonlyMode = false;
              this.allowApprove = false;
            } else if (
              matchedStatus &&
              this.localeHelperService.getLocalizedValue(matchedStatus.displayName) === 'Awaiting Creator Changes' &&
              this.requisition?.createdBy === this.userLite?.username
            ) {
              // Allow creator to edit items only when status is "Awaiting Creator Changes"
              this.readonlyMode = false;
              this.allowApprove = false;
              // Disable general information fields but keep items table editable
              this.disableGeneralFields();
            } else if (response.data.items?.length > 0 && response.data.items[0].canApproveNow) {
              this.allowApprove = true;
            }
          }

          if (
            this.readonlyMode ||
            (!this.allowApprove && this.containsStatusRequistion(['Approved', 'Pending Approval', 'Partially Approved']))
          ) {
            this.activeReadonlyMode();
          }
        },
        error: (error) => {
          this.readonlyMode = true;
          this.activeReadonlyMode();
          console.error('Error searching requisitions:', error);
        },
      });
    }
  }

  filterRequisitionStatus(statusToFind: string[] = ['Draft', 'Pending Approval']): ExternalRequisitionStatus[] {
    if (this.requisition?.id) {
      if (this.isDraftStatus()) {
        return this.allRequisitionStatus.filter((status) => statusToFind.includes(status.displayName.en));
      }
      return this.allRequisitionStatus;
    }
    return this.allRequisitionStatus.filter((status) => statusToFind.includes(status.displayName.en));
  }

  getStatusDisplayName(statusId: string): string {
    const matchedStatus = this.allRequisitionStatus.find((status) => status.id === statusId);
    return matchedStatus ? this.localeHelperService.getLocalizedValue(matchedStatus.displayName) : statusId;
  }

  showReadonlyStatus(): boolean {
    return this.containsStatusRequistion(['Approved', 'Pending Approval', 'Partially Approved', 'Approved', 'Processing']);
  }

  isDraftStatus(): boolean {
    const matchedStatus = this.allRequisitionStatus.find((status) => status.id === this.requisition?.statusReference);
    return matchedStatus ? matchedStatus.displayName.en === 'Draft' : false;
  }

  isAwaitingCreatorChangesAndIsCreator(): boolean {
    const matchedStatus = this.allRequisitionStatus.find((status) => status.id === this.requisition?.statusReference);
    const isAwaitingCreatorChanges =
      matchedStatus && this.localeHelperService.getLocalizedValue(matchedStatus.displayName) === 'Awaiting Creator Changes';
    const isCreator = this.requisition?.createdBy === this.userLite?.username;
    return !!(isAwaitingCreatorChanges && isCreator);
  }

  containsStatusRequistion(statuses: string[]): boolean {
    const statusFilter = this.allRequisitionStatus.filter((status) =>
      statuses.includes(this.localeHelperService.getLocalizedValue(status.displayName || '')),
    );
    const matchedStatus = statusFilter.find((status) => status.id === this.requisition?.statusReference);
    return matchedStatus ? statuses.includes(matchedStatus.displayName.en) : false;
  }

  get viewApprovedAllButton(): boolean {
    return this.allowApprove && this.containsStatusRequistion(['Pending Approval']);
  }

  onMakeChangeRequisition(event: { checked: boolean }) {
    if (event.checked) {
      this.itemsToRequisitionFormArray.enable();
    } else {
      this.populateRequisitionData(this.requisition!);
      this.requisitionForm.get('departmentReference')?.disable();
      this.requisitionForm.get('priority')?.disable();
      this.requisitionForm.get('requisitionType')?.disable();
      this.itemsToRequisitionFormArray.disable();
    }
    console.log(event);
  }
  allEntriesApprovedOrPending(): boolean {
    const allApproved = this.itemsToRequisitionFormArray.controls.every((control) => control.get('approved')?.value === true);
    const allNotApproved = this.itemsToRequisitionFormArray.controls.every((control) => control.get('approved')?.value === false);
    return (allApproved || allNotApproved) && this.containsStatusRequistion(['Partially Approved']);
  }

  isLastApprover(): boolean {
    return this.requisition?.requiredApprovers === (this.requisition?.approvers?.length ?? 0 + 1);
  }

  private getQuotationForThisRequisition(): void {
    const command: QuotationSearchCommand = {
      entityKey: {
        reference: this.requisition?.id ?? '',
        entityDefinitionCode: GlobalEntityDefinition.ExternalRequisition,
      },
    };
    this.quotationProxyService.search(command).subscribe({
      next: (response) => {
        this.tableQuotationsComparativeService.PushQuotationsItems(response.data || []);
      },
      error: (error) => {
        console.error('Error fetching quotations:', error);
      },
    });
  }
  onReject(): void {
    if (!this.requisition?.id) {
      GlobalMessageUtil.error('Error', 'No requisition selected');
      return;
    }

    this.confirmationService.confirm({
      message: 'Are you sure you want to reject this requisition? This action cannot be undone.',
      header: 'Reject Requisition',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes, Reject',
      rejectLabel: 'Cancel',
      key: 'main',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.isLoading++;
        // Find the "Rejected" status
        const rejectedStatus = this.allRequisitionStatus.find((status) => status.displayName.en.toLowerCase().includes('reject'));

        if (!rejectedStatus) {
          GlobalMessageUtil.error('Error', 'Rejected status not found');
          this.isLoading--;
          return;
        }

        this.requisitionService.updateRequisitionStatus(this.requisition!.id!, rejectedStatus.code).subscribe({
          next: (response) => {
            if (response.success) {
              GlobalMessageUtil.success('Success', 'Requisition rejected successfully');
              this.router.navigate(['/requisitions']);
            } else {
              GlobalMessageUtil.error('Error', 'Error rejecting requisition');
            }
            this.isLoading--;
          },
          error: (error) => {
            console.error('Error rejecting requisition:', error);
            this.isLoading--;
          },
        });
      },
    });
  }

  onclickApproveAll(): void {
    this.itemsToRequisitionFormArray.controls.forEach((control) => {
      control.get('approved')?.setValue(true);
    });
    this.isLoading++;
    this.requisitionService.approveAndSplitRequisition(this.requisition?.id || '').subscribe({
      next: (response) => {
        if (response.success) {
          GlobalMessageUtil.success('Success', `Requisition approved successfully` + this.requisition?.number);
          this.router.navigate(['/requisitions']);
        }
        this.isLoading--;
      },
      error: (error) => {
        console.error('Error approving requisition:', error);
        this.isLoading--;
      },
    });
  }

  // Helper method to check if status is in approval range (2-4)
  isStatusInApprovalRange(): boolean {
    const matchedStatus = this.allRequisitionStatus.find((status) => status.id === this.requisition?.statusReference);
    if (!matchedStatus) return false;

    const statusCode = matchedStatus.code;
    return statusCode >= 2 && statusCode <= 4;
  }

  isStatusInQuotationRange(): boolean {
    const matchedStatus = this.allRequisitionStatus.find((status) => status.id === this.requisition?.statusReference);
    if (!matchedStatus) return false;

    const statusCode = matchedStatus.code;
    return statusCode >= 4 && statusCode <= 9 && this.isChildRequisition();
  }

  isChildRequisition(): boolean {
    return !_.isNil(this.requisition?.parentRequisitionReference);
  }

  activeReadonlyMode() {
    this.departmentReferenceControl.disable();
    this.priorityControl.disable();
    this.requisitionTypeControl.disable();
    this.statusReferenceControl.disable();
    this.destinationLocationCodeControl.disable();
    if (this.hasParentRequisition()) this.requestingAssignedUserControl.enable();
    this.itemsToRequisitionFormArray.disable();
    this.visibleDeleteButton = false;
  }

  disableGeneralFields() {
    this.departmentReferenceControl.disable();
    this.priorityControl.disable();
    this.requisitionTypeControl.disable();
    this.statusReferenceControl.disable();
    this.destinationLocationCodeControl.disable();
    this.itemsToRequisitionFormArray.enable();
    this.visibleDeleteButton = true;
  }
  enableOnlyAssignedUserField() {
    this.departmentReferenceControl.disable();
    this.priorityControl.disable();
    this.requisitionTypeControl.disable();
    this.statusReferenceControl.disable();
    this.destinationLocationCodeControl.disable();
    this.requestingAssignedUserControl.enable();
    this.itemsToRequisitionFormArray.disable();
    this.visibleDeleteButton = false;
  }

  hasParentRequisition(): boolean {
    return this.canEdit() && !_.isNil(this.requisition?.parentRequisitionReference);
  }

  canEdit() {
    return (
      AuthService.hasRole(ExternalRequisitionAdminGroup.ExternalRequisitionAdmin) ||
      AuthService.hasRole(ExternalRequisitionViewerGroup.ExternalRequisitionViewer)
    );
  }

  canSaveParentRequisition(): boolean {
    return this.hasParentRequisition() && !_.isNil(this.requisition?.id);
  }

  searchUser(event: AutoCompleteCompleteEvent): void {
    const query = event.query.toLowerCase();
    // Filter users excluding the requesting user
    this.usersToDisplay = this.allUsers.filter((user) => {
      const matchesQuery = user.fieldToDisplay?.toLowerCase().includes(query) || user.username?.toLowerCase().includes(query);
      return matchesQuery;
    });

    if (this.usersToDisplay.length > 8) {
      this.usersToDisplay = this.usersToDisplay.slice(0, 8);
    }
  }
  reloadPage() {
    this.loadInitialData();
    if (!this.requisition) return;
    this.getRequisitionById(this.requisition.id!);
    this.crt.detectChanges();
  }

  downloadRequisitionPdf(): void {
    if (!this.requisition?.id) {
      GlobalMessageUtil.error('Error', 'No requisition selected');
      return;
    }
    this.isLoading++;
    this.requisitionService.downloadRequisitionPdf(this.requisition.id).subscribe({
      next: (blob: Blob) => {
        this.isLoading--;
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      },
      error: (error) => {
        console.error('Error downloading requisition PDF:', error);
        this.isLoading--;
      },
    });
  }
}
